import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { verifyMagicLink } from "../api/client";

type Status = "verifying" | "error";

/**
 * Redeems a magic-link token and signs the user in.
 *
 * This page is the reason `APP_BASE_URL` must point at the frontend origin
 * rather than the API: the API deliberately exposes no GET redemption route,
 * because email scanners and link previewers follow GET links and would burn
 * the single-use token before the user ever clicked it. So the emailed link
 * lands here, and the browser makes the POST.
 */
export default function AuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("verifying");
  const [message, setMessage] = useState("");

  const token = params.get("token");

  // Tokens are single-use, so a duplicate request would redeem the token and
  // then fail on the second call. React 18 StrictMode double-invokes effects
  // in development, which makes that a certainty without this guard.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setStatus("error");
      setMessage("This sign-in link is missing its token. Please request a new one.");
      return;
    }

    void (async () => {
      try {
        await verifyMagicLink(token);
        // Replace so the token never stays in history: pressing Back would
        // otherwise return to a URL containing a credential.
        navigate("/", { replace: true });
      } catch {
        setStatus("error");
        setMessage(
          "This sign-in link is no longer valid. Links expire and can only be used once.",
        );
      }
    })();
  }, [token, navigate]);

  if (status === "verifying") {
    return (
      <main
        className="flex min-h-screen items-center justify-center px-4"
        data-testid="auth-callback-verifying"
      >
        <div className="text-center">
          <div
            className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-emerald-600 dark:border-[#2e2e2e] dark:border-t-emerald-400"
            role="status"
            aria-label="Signing you in"
          />
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Signing you in…</p>
        </div>
      </main>
    );
  }

  return (
    <main
      className="flex min-h-screen items-center justify-center px-4"
      data-testid="auth-callback-error"
    >
      <div className="w-full max-w-sm rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-[#2e2e2e] dark:bg-[#1a1a1a]">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Sign-in link expired
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{message}</p>
        <Link
          to="/login"
          className="mt-5 inline-block rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
          data-testid="auth-callback-retry"
        >
          Request a new link
        </Link>
      </div>
    </main>
  );
}
